import type { Translate } from 'next-translate';
import type { FC } from 'react';
import { AddCircleOutline, AlertCircleFilled } from '@eqi-investimentos/icons/web';
import { Button, Flex, Skeleton, Text } from '@eqi-investimentos/react';
import { WalletInvestmentCategory } from '@/modules/Wallet/interface/walet';
import { useGetWalletInvestiments } from '@/modules/Wallet/http/client/useGetWalletInvestiments';
import { MyWalletCategoriesChart } from '@/modules/Wallet/ui/MyWalletCategoriesChart';
import { MyWalletCategoriesTable } from '@/modules/Wallet/ui/MyWalletCategoriesTable';
import { useConfig } from '@/contexts/ConfigContext';
import { Thumb } from '@/components/atoms/Thumb';
import { IfRender, MapRender } from '@/utils/jsx';
export const InvestmentsCategoriesCard: FC<InvestmentsCategoriesCardProps> = ({
  openConnectorsDrawer,
  t
}) => {
  const {
    isMobile
  } = useConfig();
  const {
    data: myInvestiments = {
      strategies: [],
      categories: [],
      subcategories: []
    },
    isFetching
  } = useGetWalletInvestiments();
  const hasNoCategories = myInvestiments?.categories?.length === 0;
  const chartAssetCategories = mountChartDataCategoriesAssets(myInvestiments?.categories);
  return <Flex display={{
    base: 'none',
    md: 'flex'
  }} flexDir="column" rowGap={hasNoCategories ? '1.875rem' : '3.75rem'} p="2rem" w="25rem" h="26.25rem" bgColor="neutral.50" border="1px solid" borderColor="transparency.light" borderRadius="lg" data-sentry-element="Flex" data-sentry-component="InvestmentsCategoriesCard" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
      <IfRender condition={isFetching} data-sentry-element="IfRender" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
        <Skeleton w="12.5rem" h="1.625rem" data-sentry-element="Skeleton" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
      </IfRender>
      <IfRender condition={!isFetching} data-sentry-element="IfRender" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
        <Text fontSize="lg" fontWeight="normal" lineHeight="7" color="content.primary" data-sentry-element="Text" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
          {t('screens.myWallet.chartCategories.title')}
        </Text>
      </IfRender>
      <Flex alignItems="center" gap="0.625rem" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
        <IfRender condition={isFetching} data-sentry-element="IfRender" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
          <Flex w="full" flexDir="row" alignItems="flex-start" justifyContent="center" gap="1.875rem" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
            <Skeleton w="10rem" h="8.75rem" borderRadius="full" data-sentry-element="Skeleton" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />

            <Flex w="10rem" flexDir="column" gap="1.25rem" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
              <MapRender items={Array.from({
              length: 4
            })} render={(_, idx) => <Skeleton key={idx} w="10rem" h="1.25rem" />} data-sentry-element="MapRender" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
            </Flex>
          </Flex>
        </IfRender>
        <IfRender condition={hasNoCategories && !isFetching} data-sentry-element="IfRender" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
          <Flex flexDir="column" justifyContent="center" alignItems="center" gap="2.5rem" maxW="100%" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
            <Flex gap="3" px="1rem" py="0.75rem" maxW="24.375rem" bgColor="background.brandLight" borderRadius="lg" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
              <AlertCircleFilled mt="0.5" w="24" h="24" color="content.brand" data-sentry-element="AlertCircleFilled" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
              <Text w="calc(100% - 2.8125rem)" fontSize="sm" lineHeight="1.125rem" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
                {t('screens.myWallet.chartCategories.noData')}
              </Text>
            </Flex>
            <Flex justifyContent="center" alignItems="center" w="full" gap="1.875rem" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
              <Thumb src="/assets/icons/svg/chart_donut_icon.svg" width={45} height={45} alt="icon chart donut" data-sentry-element="Thumb" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
              <Text fontSize="md" fontWeight="light" lineHeight="7" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
                {t('screens.myWallet.seeInvestments')}
              </Text>
            </Flex>
            <Flex justifyContent="center" alignItems="center" w="full" gap="1.875rem" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
              <Button variant="solid" onClick={openConnectorsDrawer} w="21.3125rem" h="3.25rem" borderRadius="0.25rem" data-sentry-element="Button" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
                <Flex gap="1.5rem" data-sentry-element="Flex" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
                  <Text fontSize="sm" fontWeight="semibold" lineHeight="5" color="content.alwaysLight" data-sentry-element="Text" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
                    {t('screens.myWallet.addInstitution')}
                  </Text>
                  <AddCircleOutline width={7} height={7} color="content.alwaysLight" data-sentry-element="AddCircleOutline" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
                </Flex>
              </Button>
            </Flex>
          </Flex>
        </IfRender>
        <IfRender condition={!hasNoCategories && !isFetching} data-sentry-element="IfRender" data-sentry-source-file="InvestmentsCategoriesCard.tsx">
          <MyWalletCategoriesChart isLoading={isFetching} assets={chartAssetCategories} isMobile={isMobile} data-sentry-element="MyWalletCategoriesChart" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
          <MyWalletCategoriesTable isLoading={isFetching} categories={chartAssetCategories} isDesktop={false} data-sentry-element="MyWalletCategoriesTable" data-sentry-source-file="InvestmentsCategoriesCard.tsx" />
        </IfRender>
      </Flex>
    </Flex>;
};
const mountChartDataCategoriesAssets = (data: Omit<WalletInvestmentCategory, 'investments'>[]) => data.map(({
  name,
  percentage,
  total
}) => ({
  name,
  total,
  percentage: parseFloat(percentage.toFixed(2))
}));
type InvestmentsCategoriesCardProps = {
  openConnectorsDrawer: () => void;
  t: Translate;
};